<template>
  <div class="category">
    <Container>
      <CommonBreadcrumbs :breadcrumbs="breadcrumbsUpdated" />

      <template v-if="showProducts">
        <Catalog :title="title">
            <template #title="{ catalogTitle }">
              <CatalogTitle
                :title="catalogTitle"
                :styles="
                  !showProducts
                    ? 'font-size: 2.4rem;line-height: 3.2rem;margin-bottom: 1.6rem'
                    : ''
                "
              />
            </template>

            <template v-if="showCategories" #category>
              <CatalogCategory
                v-show="!fetching"
                :categories="children"
                mobile-scroll
              />

              <CatalogCategoryPreload v-if="fetching" mobile-scroll />
            </template>

            <template #list>
              <CatalogProductsList
                block="category"
                :is-category-monitor="isCategoryMonitor"
              />
            </template>
          </Catalog>

        <div v-if="hasSeoText" style="margin: 4rem 0">
          <Description :value="seoText" :cropped-height="200" />
        </div>
      </template>

      <template v-else>
        <client-only>
          <CatalogTitlePreload
            v-if="fetching"
            class="category__preload-title"
          />
        </client-only>

        <CatalogTitle
          v-if="!fetching"
          :title="title"
          styles="font-size: 2.4rem;line-height: 3.2rem;"
        />

        <client-only>
          <CatalogCategoryPreload v-if="fetching" />
        </client-only>

        <CatalogCategory
          v-show="!fetching"
          :categories="children"
        />
      </template>
    </Container>

    <Container v-if="!showProducts" class-name="from-tablet">
      <HomeRubricSwiper :products="rubricProducts" block="interested">
        <template #title>
          {{ $t('common.interested_products') }}
        </template>
      </HomeRubricSwiper>
    </Container>
  </div>
</template>

<script setup lang="ts">
import {
  BRAND_GROUP_FILTER_CODE,
  CONDITION_FILTER_CODE,
  CONDITION_FILTER_NEW_UUID,
  FILTER_SPLIT_KEY
} from '~/constants/common'
import type { Breadcrumb } from '~/types/common'
import type { MenuCategoryItem } from '~/types/catalog'

const { siteUrl, cdnUrl } = useRuntimeConfig().public
const { $gtm } = useNuxtApp()
const route = useRoute()
const i18n = useI18n()
const localePath = useLocalePath()
const { unreadMessages } = useMessenger()

const commonStore = useCommonStore()
const commonCategoriesStore = useCommonCategoriesStore()
const catalogCategoryStore = useCatalogCategoryStore()
const catalogFiltersStore = useCatalogFiltersStore()
const catalogProductsStore = useCatalogProductsStore()
const catalogStore = useCatalogStore()

const isFirstMount = useState('isFirstMount', () => false)

const {
  fetching,
  name: categoryName,
  name_sing: categoryNameSingular,
  widthProducts,
  breadcrumbs,
  children,
  seoKeywords,
  seo_text: seoText,
  parent: categoryParent,
  uuid: categoryUuid
} = storeToRefs(catalogCategoryStore)

const {
  lowPrice,
  highPrice,
  filterQueryString,
  currentPage,
  queryValue,
  selectedBrand,
  selectedCondition,
  productsCount
} = storeToRefs(catalogFiltersStore)

const rubricProducts = computed(() => {
  return catalogProductsStore.list.slice(0, 16)
})

const {
  firstFourImage
} = storeToRefs(catalogProductsStore)

const categoryFilters = computed(() => {
  return unref(route).params.categoryFilters || undefined
})

const isCategoryMonitor = computed(() => {
  return ['C644049', 'C320580'].includes(route.params.categoryId as string)
})

provide('filterWithLinks', true)
provide('selectedBrand', selectedBrand)
provide('selectedCondition', selectedCondition)

const title = computed<string>(() => (
  [
    categoryName.value,
    selectedBrand.value?.label,
    selectedCondition.value?.value !== CONDITION_FILTER_NEW_UUID ? selectedCondition.value?.label : false
  ].filter(Boolean).join(' ')
))

const breadcrumbsUpdated = computed<Breadcrumb[]>(() => {
  const breadcrumbsCatalog = breadcrumbs.value.map(({ name, slug, uuid }) => ({
    label: name,
    link: `/category/${slug}/${uuid}`,
  }))
  const lastBreadcrumb = breadcrumbsCatalog.at(-1)

  if (selectedBrand.value && lastBreadcrumb) {
    breadcrumbsCatalog.push({
      label: selectedBrand.value.label,
      link: `${lastBreadcrumb.link}/${selectedBrand.value.code}=${selectedBrand.value.value}`
    })
  } else if (selectedCondition.value && selectedCondition.value.value !== CONDITION_FILTER_NEW_UUID && lastBreadcrumb) {
    breadcrumbsCatalog.push({
      label: selectedCondition.value.label,
      link: `${lastBreadcrumb.link}/${selectedCondition.value.code}=${selectedCondition.value.value}`
    })
  }

  return breadcrumbsCatalog
})

const staticCategory = computed<MenuCategoryItem | undefined>(() => {
  return commonCategoriesStore.listMap[categoryUuid.value]
})
const showCategories = computed(() => {
  return Boolean(staticCategory.value?.children?.length) || children.value.length > 0
})

const showProducts = computed(() => {
  const staticCategoryParent = staticCategory.value?.parent

  return (
    widthProducts.value ||
    categoryParent.value !== null ||
    (catalogCategoryStore.fetchingContent && staticCategoryParent !== null)
  )
})

const pageTitle = computed(() => {
  return commonStore.isFaviconMsg
    ? i18n.t('messenger.unread', { count: unreadMessages.value })
    : seoTitle.value
})

const seoTitle = computed(() => {
  let title = ''
  const page = unref(route).query.page
  const condition = selectedCondition.value?.label
  const brand = selectedBrand.value?.label
  const filter = [brand, condition].filter(Boolean).join(', ')

  if (page) {
    title = i18n.t('common.is_page', { page }) + ' '
  }


  if (brand || condition) {
    title += i18n.t('common.category_filter_title', {
      categoryName: categoryName.value,
      categoryNameSingular: categoryNameSingular.value || categoryName.value,
      filter
    })
  } else {
    title += i18n.t(
      'common.category_title', {
        categoryName: categoryName.value,
        categoryNameSingular: categoryNameSingular.value || categoryName.value
      }
    )
  }

  return title
})

const hasSeoText = computed(() => (
  seoText.value.length &&
  !unref(route).query.page &&
  !categoryFilters.value?.length
))

const headDescription = computed(() => {
  return i18n.t('common.category_description', { categoryName: seoTitle.value })
})

const headKeywords = computed(() => {
  if (selectedCondition.value || selectedBrand.value || !seoKeywords.value) {
    const customKeywords = [categoryName.value]

    if (categoryName.value !== title.value) {
      customKeywords.push(title.value)
    }
    customKeywords.push(i18n.t('common.category_keywords') as string)

    return customKeywords.join(', ')
  }

  return seoKeywords.value
})

// Allow indexing if
// 1) ordering not selected
// 2) selected only filter brand or/and filter condition
const isIndexingAllowed = computed(() => {
  const hasOrdering = Boolean(unref(route).query.ordering)
  // Нормалізація categoryFilters.value до масиву рядків
  const filtersArray = (Array.isArray(categoryFilters.value)
    ? categoryFilters.value
    : categoryFilters.value?.split(FILTER_SPLIT_KEY)) || []

  // Фільтруємо заборонені фільтри (без бренду та стану)
  const notAllowedSelectedFilters = filtersArray.filter(
    (filter) => !filter.includes(BRAND_GROUP_FILTER_CODE) &&
      !filter.includes(CONDITION_FILTER_CODE)
  )

  return (
    !hasOrdering && // Hasn't ordering
    notAllowedSelectedFilters.length === 0 // Hasn't selected not allowed filters
  )
})

const { error, status } = await useLazyAsyncData(async () => {
  const { params, query } = route
  const categoryFilters = params.categoryFilters?.toString()
  const response = await catalogStore.getCatalog({
    uuid: params.categoryId as string,
    categoryFilters,
    query
  })

  // Відправляється аналітика коли перемикаємо сторінку або використовуємо фільтри, сортування
  if (import.meta.client) {
    $gtm.viewCatalogPage({
      items: response.products.slice(0, 10).map((product) => ({
        id: product.offer_main.code,
        name: product.name,
        categoryName: product.category.name,
        price: product.offer_main?.price_promo || product.offer_main.price
      }))
    })
  }

  if (response.slug !== params.categorySlug) {
    navigateTo(
      {
        params: { ...params, categorySlug: response.slug },
        query
      },
      { redirectCode: 301 }
    )

    return null
  }

  return response
}, {
  watch: [() => route.query],
})

if (error.value) {
  showError(error.value)
}

watch(queryValue, (value) => navigateTo(
  { query: value }
), { deep: true })

watch(filterQueryString, async (value: string) => {
  const { categorySlug, categoryId } = unref(route).params
  // Формуємо масив параметрів для маршруту
  const paramsList = [categorySlug, categoryId, value].filter(Boolean)

  await navigateTo({
    path: localePath(`/category/${paramsList.join('/')}`),
    query: queryValue.value
  })
})
//

const breadcrumbsJSOLD = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: i18n.t('common.home'),
      item: `https://synthetic.ua${i18n.locale.value !== 'ua' ? `/${i18n.locale.value}` : ''}`
    },
    ...breadcrumbsUpdated.value.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 2,
      name: item.label,
      item: `${siteUrl}/${item.link}`
    }))
  ]
}))

const productJSOLD = computed(() => ({
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: seoTitle.value,
  image: getImagePath({ name: firstFourImage.value[0], size: '300x300' }),
  description: headDescription.value,
  brand: {
    '@type': 'Brand',
    name: 'Synthetic'
  },
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'UAH',
    lowPrice: lowPrice.value,
    highPrice: highPrice.value,
    offerCount: productsCount.value,
    availability: 'http://schema.org/InStock'
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: catalogCategoryStore.review_avg_rating,
    bestRating: catalogCategoryStore.review_best_rating,
    worstRating: catalogCategoryStore.review_worst_rating,
    ratingCount: catalogCategoryStore.reviews_count
  }
}))

onMounted(() => {
  // Відправляємо метрики при першому рендерингу категорії по прямому посиланню
  if (!isFirstMount.value && status.value === 'success') {
    isFirstMount.value = true

    $gtm.viewCatalogPage({
      items: catalogProductsStore.list.slice(0, 10).map((product) => ({
        id: product.offer_main.code,
        name: product.name,
        categoryName: product.category.name,
        price: product.offer_main?.price_promo || product.offer_main.price
      }))
    })
  }
})

useHead({
  link: () => [
    ...firstFourImage.value.map((name: string) => ({
      rel: 'preload',
      as: 'image',
      href: getImagePath({ name, size: '300x300' }, cdnUrl as string)
    })),
    ...getCanonicalCatalog(`${siteUrl}${route.fullPath}`, currentPage.value)
  ],
  script: [
    { innerHTML: JSON.stringify(breadcrumbsJSOLD.value), type: 'application/ld+json' },
    { innerHTML: JSON.stringify(productJSOLD.value), type: 'application/ld+json' }
  ]
})

useSeoMeta({
  title: () => pageTitle.value,
  description: () => headDescription.value,
  keywords: () => headKeywords.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => headDescription.value,
  twitterTitle: () => seoTitle.value,
  twitterDescription: () => headDescription.value,
  robots: () => getRobots(isIndexingAllowed.value, isIndexingAllowed.value)
})
</script>

<style lang="scss">
.category {
  &__preload-title {
    margin-bottom: 1.6rem;
  }

  &__title {
    font-size: 2.4rem;
    line-height: 3.2rem;
  }
}
</style>
